import type { LayerRenderer } from '../registry'

/**
 * Fonds du médaillon (slot `background`). Peints en premier, clippés au
 * disque r 53 centré (60,60). Style « peint vectoriel » : dégradés doux,
 * lumière haut-gauche. Ids de gradients TOUJOURS préfixés par `uid`.
 */
export const BACKGROUNDS: Record<string, LayerRenderer> = {
  bg_ocean_profond: ({ uid }) => (
    <>
      <defs>
        <radialGradient id={`${uid}-bg`} cx="0.42" cy="0.32" r="0.85">
          <stop offset="0" stopColor="#16758F" />
          <stop offset="0.55" stopColor="#0F5B70" />
          <stop offset="1" stopColor="#082F3C" />
        </radialGradient>
        <radialGradient id={`${uid}-halo`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#1E96B5" stopOpacity="0.35" />
          <stop offset="1" stopColor="#1E96B5" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-vign`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#041E28" stopOpacity="0" />
          <stop offset="1" stopColor="#041E28" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      <circle cx="60" cy="60" r="53" fill={`url(#${uid}-bg)`} />
      {/* halo lumineux derrière la tête */}
      <circle cx="58" cy="46" r="36" fill={`url(#${uid}-halo)`} />
      {/* assombrissement du bord bas (vignette partielle, mangée par le clip) */}
      <rect x="7" y="74" width="106" height="40" fill={`url(#${uid}-vign)`} />
    </>
  ),
  bg_crepuscule: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-dusk`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#E8935A" />
          <stop offset="0.52" stopColor="#7A3E5E" />
          <stop offset="1" stopColor="#1E2445" />
        </linearGradient>
        <radialGradient id={`${uid}-sunglow`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#F5CD6B" stopOpacity="0.85" />
          <stop offset="0.45" stopColor="#F0A860" stopOpacity="0.35" />
          <stop offset="1" stopColor="#F5CD6B" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-halo`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#F0A860" stopOpacity="0.3" />
          <stop offset="1" stopColor="#F0A860" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-nuage`} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor="#3A2B50" />
          <stop offset="1" stopColor="#221838" />
        </linearGradient>
        <linearGradient id={`${uid}-mer`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#46284E" />
          <stop offset="1" stopColor="#141A38" />
        </linearGradient>
        <linearGradient id={`${uid}-vign`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#101430" stopOpacity="0" />
          <stop offset="1" stopColor="#101430" stopOpacity="0.6" />
        </linearGradient>
      </defs>
      {/* plan 1 : ciel en 3 stops + soleil à 3 valeurs (glow, disque, cœur) */}
      <rect x="0" y="0" width="120" height="120" fill={`url(#${uid}-dusk)`} />
      <circle cx="42" cy="48" r="26" fill={`url(#${uid}-sunglow)`} />
      <circle cx="42" cy="48" r="9" fill="#F5CD6B" />
      <circle cx="40" cy="46" r="7.5" fill="#F7E3A0" opacity="0.55" />
      {/* plan 2 : bancs de nuages modelés — base gradient, ventre ombré, crête embrasée */}
      <path d="M 4 36 Q 16 29 30 33 Q 38 27 50 31 Q 62 29 72 35 Q 60 41 44 40 Q 22 43 4 36 Z" fill={`url(#${uid}-nuage)`} opacity="0.9" />
      <path d="M 10 36.6 Q 26 40 44 39.4 Q 58 40.2 70 35.6 Q 60 40.4 44 39.8 Q 24 42 10 36.6 Z" fill="#161028" opacity="0.55" />
      <path d="M 22 31.6 Q 34 28.4 46 30.6 Q 56 29.2 66 33" fill="none" stroke="#F0A860" strokeWidth="1.1" strokeLinecap="round" opacity="0.55" />
      <path d="M 58 63 Q 72 56 88 60 Q 102 56 118 63 Q 104 69 86 67 Q 70 70 58 63 Z" fill={`url(#${uid}-nuage)`} />
      <path d="M 64 63.4 Q 80 67 96 65.4 Q 108 66 116 62.8 Q 104 68.4 86 66.6 Q 71 69.2 64 63.4 Z" fill="#161028" opacity="0.5" />
      <path d="M 100 58.4 Q 110 58.2 117 62.4" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.6" />
      <path d="M 6 51 Q 30 47 58 49" fill="none" stroke="#F0A860" strokeWidth="1.6" strokeLinecap="round" opacity="0.35" />
      {/* halo doux derrière la tête */}
      <circle cx="58" cy="46" r="36" fill={`url(#${uid}-halo)`} />
      {/* plan 3 : mer basse + traînée du couchant + vignette */}
      <path d="M 0 88 Q 30 84 60 87 Q 92 84 120 88 L 120 120 L 0 120 Z" fill={`url(#${uid}-mer)`} />
      <path d="M 30 90 Q 40 88.6 50 90 M 36 96 Q 44 94.8 52 96" fill="none" stroke="#F0A860" strokeWidth="1.4" strokeLinecap="round" opacity="0.45" />
      <rect x="0" y="82" width="120" height="38" fill={`url(#${uid}-vign)`} />
    </>
  ),
  bg_brume: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-mist`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#9FBFC8" />
          <stop offset="0.55" stopColor="#5A7E8C" />
          <stop offset="1" stopColor="#2E4A56" />
        </linearGradient>
        <radialGradient id={`${uid}-soleil`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#EDF3F4" stopOpacity="0.65" />
          <stop offset="1" stopColor="#EDF3F4" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-halo`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#C4D8DC" stopOpacity="0.3" />
          <stop offset="1" stopColor="#C4D8DC" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-roc`} x1="0" y1="0" x2="0.35" y2="1">
          <stop offset="0" stopColor="#35545F" />
          <stop offset="1" stopColor="#182E38" />
        </linearGradient>
        <linearGradient id={`${uid}-vign`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#14262E" stopOpacity="0" />
          <stop offset="1" stopColor="#14262E" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      {/* plan 1 : ciel laiteux + soleil voilé à 3 valeurs */}
      <rect x="0" y="0" width="120" height="120" fill={`url(#${uid}-mist)`} />
      <circle cx="40" cy="26" r="19" fill={`url(#${uid}-soleil)`} />
      <circle cx="40" cy="26" r="11" fill="#DCE8EA" opacity="0.4" />
      <circle cx="38.6" cy="24.6" r="7.5" fill="#EDF3F4" opacity="0.5" />
      {/* halo doux derrière la tête */}
      <circle cx="58" cy="46" r="36" fill={`url(#${uid}-halo)`} />
      {/* plan 2 : récif modelé — base gradient, face basse ombrée, arête claire, rim droit */}
      <path d="M 62 84 Q 74 66 90 72 Q 100 66 118 80 L 118 96 L 62 96 Z" fill={`url(#${uid}-roc)`} />
      <path d="M 78 86 Q 90 78 104 82 Q 112 84 118 88 L 118 96 L 78 96 Z" fill="#10222B" opacity="0.6" />
      <path d="M 74 74 Q 80 70.5 88 71.8" fill="none" stroke="#6E93A0" strokeWidth="1.1" strokeLinecap="round" opacity="0.6" />
      <path d="M 100 68.4 Q 110 71 117 79" fill="none" stroke="#E5CC8F" strokeWidth="1" strokeLinecap="round" opacity="0.5" />
      {/* plan 3 : nappes de brume étagées (ombre portée sous la nappe, filet clair dessus) */}
      <path d="M 4 52 Q 26 47 52 50 Q 82 46 116 52 Q 84 57 54 55 Q 26 58 4 52 Z" fill="#DCE8EA" opacity="0.28" />
      <path d="M 8 53.5 Q 34 55.5 62 54 Q 92 55.5 114 52.8" fill="none" stroke="#41616E" strokeWidth="0.9" strokeLinecap="round" opacity="0.4" />
      <path d="M 0 72 Q 24 66 56 70 Q 90 65 120 72 Q 92 78 58 75 Q 26 79 0 72 Z" fill="#DCE8EA" opacity="0.42" />
      <path d="M 10 69.5 Q 34 66.5 58 69" fill="none" stroke="#F2F7F7" strokeWidth="1.2" strokeLinecap="round" opacity="0.5" />
      <path d="M 4 94 Q 32 88 64 92 Q 94 88 118 94 Q 90 100 60 97 Q 30 101 4 94 Z" fill="#DCE8EA" opacity="0.34" />
      <rect x="0" y="82" width="120" height="38" fill={`url(#${uid}-vign)`} />
    </>
  ),
  bg_tempete: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-storm`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2E3A48" />
          <stop offset="1" stopColor="#10161E" />
        </linearGradient>
        <linearGradient id={`${uid}-nuage`} x1="0" y1="0" x2="0.25" y2="1">
          <stop offset="0" stopColor="#28333F" />
          <stop offset="1" stopColor="#141C26" />
        </linearGradient>
        <radialGradient id={`${uid}-flash`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#F5EDD7" stopOpacity="0.4" />
          <stop offset="1" stopColor="#F5EDD7" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-halo`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#7E93A8" stopOpacity="0.26" />
          <stop offset="1" stopColor="#7E93A8" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-mer`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#24404C" />
          <stop offset="1" stopColor="#0A1016" />
        </linearGradient>
        <linearGradient id={`${uid}-vign`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#05080C" stopOpacity="0" />
          <stop offset="1" stopColor="#05080C" stopOpacity="0.5" />
        </linearGradient>
      </defs>
      <rect x="0" y="0" width="120" height="120" fill={`url(#${uid}-storm)`} />
      {/* plan 1 : plafond nuageux — base gradient, ventre sombre, crête claire */}
      <path d="M 0 30 Q 14 16 32 22 Q 42 12 58 18 Q 72 12 84 22 Q 100 16 120 28 L 120 0 L 0 0 Z" fill={`url(#${uid}-nuage)`} />
      <path d="M 0 30 Q 14 16 32 22 Q 42 12 58 18 Q 72 12 84 22 Q 100 16 120 28" fill="none" stroke="#0C121A" strokeWidth="1.6" opacity="0.6" />
      <path d="M 0 34 Q 18 24 38 29 Q 54 22 70 30 Q 88 25 108 33 Q 84 40 58 37 Q 28 41 0 34 Z" fill="#3E4E60" opacity="0.55" />
      <path d="M 6 30.5 Q 20 23.5 36 27.2" fill="none" stroke="#5A6E84" strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
      {/* lueur de l'éclair diffusée dans les nuages */}
      <circle cx="85" cy="34" r="24" fill={`url(#${uid}-flash)`} />
      {/* plan 2 : banc sombre + rim clair à droite, halo froid derrière la tête */}
      <path d="M 44 60 Q 62 52 84 57 Q 104 53 120 60 L 120 44 Q 98 40 78 46 Q 58 42 44 50 Z" fill="#141C26" opacity="0.8" />
      <path d="M 96 44.6 Q 110 43.4 119 45.8" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.5" />
      <circle cx="58" cy="46" r="36" fill={`url(#${uid}-halo)`} />
      {/* éclair : halo large + trait net (géométrie conservée) */}
      <path d="M 88 26 L 79 48 L 88 50 L 76 76" fill="none" stroke="#F5EDD7" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round" opacity="0.25" />
      <path d="M 88 26 L 79 48 L 88 50 L 76 76" fill="none" stroke="#F5EDD7" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
      {/* plan 3 : mer démontée — base gradient, crête claire, écume, creux sombres */}
      <path d="M 0 100 Q 16 92 34 98 Q 52 90 72 97 Q 92 91 120 99 L 120 120 L 0 120 Z" fill={`url(#${uid}-mer)`} />
      <path d="M 0 100 Q 16 92 34 98 Q 52 90 72 97 Q 92 91 120 99" fill="none" stroke="#5FBDD0" strokeWidth="1.6" strokeLinecap="round" opacity="0.45" />
      <path d="M 8 96 Q 24 90 40 95 M 66 93 Q 84 87 102 93" fill="none" stroke="#5FBDD0" strokeWidth="2" strokeLinecap="round" opacity="0.3" />
      <path d="M 30 104 Q 44 100 58 104 M 76 102 Q 90 98 104 102" fill="none" stroke="#0A1016" strokeWidth="1.6" strokeLinecap="round" opacity="0.6" />
      <rect x="0" y="86" width="120" height="34" fill={`url(#${uid}-vign)`} />
    </>
  ),
  bg_ile_au_tresor: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-lagoon`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#BFE4ED" />
          <stop offset="0.45" stopColor="#5FBDD0" />
          <stop offset="1" stopColor="#1E96B5" />
        </linearGradient>
        <radialGradient id={`${uid}-soleil`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#F5EDD7" stopOpacity="0.8" />
          <stop offset="0.5" stopColor="#F5CD6B" stopOpacity="0.35" />
          <stop offset="1" stopColor="#F5CD6B" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-halo`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#DFF2F6" stopOpacity="0.35" />
          <stop offset="1" stopColor="#DFF2F6" stopOpacity="0" />
        </radialGradient>
        <linearGradient id={`${uid}-sable`} x1="0" y1="0" x2="0.3" y2="1">
          <stop offset="0" stopColor="#F2DCA6" />
          <stop offset="1" stopColor="#D3AC66" />
        </linearGradient>
        <linearGradient id={`${uid}-vign`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0F5B70" stopOpacity="0" />
          <stop offset="1" stopColor="#0F5B70" stopOpacity="0.45" />
        </linearGradient>
      </defs>
      {/* plan 1 : lagon + soleil à 3 valeurs, halo derrière la tête */}
      <rect x="0" y="0" width="120" height="120" fill={`url(#${uid}-lagoon)`} />
      <circle cx="34" cy="18" r="16" fill={`url(#${uid}-soleil)`} />
      <circle cx="34" cy="18" r="8" fill="#F5EDD7" />
      <circle cx="32.8" cy="16.8" r="5.5" fill="#FDF8EA" opacity="0.7" />
      <circle cx="58" cy="46" r="36" fill={`url(#${uid}-halo)`} />
      {/* plan 2 : horizon lumineux */}
      <path d="M 0 70 Q 30 66 60 69 Q 92 66 120 70 L 120 74 Q 90 71 60 73 Q 28 71 0 74 Z" fill="#BFE4ED" opacity="0.4" />
      <path d="M 6 70.6 Q 32 67.6 60 70 Q 90 67.4 114 70.4" fill="none" stroke="#EAF7FA" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
      {/* plan 3 : plage — base gradient, sable mouillé, liseré d'écume */}
      <path d="M 58 90 Q 74 76 92 80 Q 106 82 120 90 L 120 106 L 58 106 Z" fill={`url(#${uid}-sable)`} />
      <path d="M 74 84 Q 92 79 110 85 Q 118 88 120 92 L 120 100 Q 96 92 74 92 Z" fill="#C9A55C" opacity="0.55" />
      <path d="M 60 89.4 Q 74 77.6 92 81.2" fill="none" stroke="#FBF2D8" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      {/* palmier : tronc à 3 valeurs (base + ombre gauche + rim droit) */}
      <path d="M 93 80 C 92 72 90 64 85 57" fill="none" stroke="#7A3E1F" strokeWidth="3" strokeLinecap="round" />
      <path d="M 92.2 78 C 91.4 71.5 89.7 65 86 58.8" fill="none" stroke="#5A2C14" strokeWidth="1.1" strokeLinecap="round" opacity="0.7" />
      <path d="M 93.8 77 C 93.2 71 91.6 64.8 88 58.4" fill="none" stroke="#E5CC8F" strokeWidth="0.9" strokeLinecap="round" opacity="0.6" />
      {/* palmes : base + nervures ombrées dessous + lumière dessus */}
      <path d="M 85 57 Q 74 54 66 59 Q 76 50 85 55 Z" fill="#2E7D4F" />
      <path d="M 85 57 Q 78 48 68 47 Q 80 43 87 53 Z" fill="#2E7D4F" />
      <path d="M 85 57 Q 92 47 102 47 Q 92 44 84 53 Z" fill="#2E7D4F" />
      <path d="M 85 57 Q 96 55 103 61 Q 94 50 85 54 Z" fill="#2E7D4F" />
      <path d="M 85 57 Q 76 54.6 68.5 58.4 M 85 56.6 Q 94 55.4 101 60" fill="none" stroke="#1F5C38" strokeWidth="1.2" strokeLinecap="round" opacity="0.75" />
      <path d="M 84 51.6 Q 76.5 48.4 70 47.6 M 86.5 51.4 Q 93 47.8 100.5 47.2" fill="none" stroke="#4FA871" strokeWidth="1" strokeLinecap="round" opacity="0.8" />
      {/* reflets d'eau + vignette */}
      <path d="M 8 82 Q 20 79 32 82 M 22 94 Q 36 91 50 94" fill="none" stroke="#BFE4ED" strokeWidth="1.8" strokeLinecap="round" opacity="0.5" />
      <rect x="0" y="86" width="120" height="34" fill={`url(#${uid}-vign)`} />
    </>
  ),
  bg_bataille: ({ uid }) => (
    <>
      <defs>
        <linearGradient id={`${uid}-darksea`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#26333E" />
          <stop offset="1" stopColor="#0D141A" />
        </linearGradient>
        <radialGradient id={`${uid}-feu`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#F0A860" stopOpacity="0.5" />
          <stop offset="0.6" stopColor="#E8935A" stopOpacity="0.22" />
          <stop offset="1" stopColor="#E8935A" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-halo`} cx="0.5" cy="0.5" r="0.5">
          <stop offset="0" stopColor="#8C9CAC" stopOpacity="0.25" />
          <stop offset="1" stopColor="#8C9CAC" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={`${uid}-fumee`} cx="0.38" cy="0.34" r="0.9">
          <stop offset="0" stopColor="#A89E90" />
          <stop offset="1" stopColor="#5E574E" />
        </radialGradient>
        <linearGradient id={`${uid}-vign`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#05080C" stopOpacity="0" />
          <stop offset="1" stopColor="#05080C" stopOpacity="0.55" />
        </linearGradient>
      </defs>
      {/* plan 1 : nuit + halo derrière la tête + lueur d'incendie derrière l'épave */}
      <rect x="0" y="0" width="120" height="120" fill={`url(#${uid}-darksea)`} />
      <circle cx="58" cy="46" r="36" fill={`url(#${uid}-halo)`} />
      <circle cx="42" cy="80" r="30" fill={`url(#${uid}-feu)`} />
      {/* plan 2 : fumées modelées (base gradient + ventre chauffé par le feu) */}
      <circle cx="58" cy="64" r="7" fill={`url(#${uid}-fumee)`} opacity="0.5" />
      <circle cx="68" cy="56" r="9" fill={`url(#${uid}-fumee)`} opacity="0.42" />
      <circle cx="80" cy="48" r="11" fill={`url(#${uid}-fumee)`} opacity="0.32" />
      <path d="M 53 68 Q 58 70.5 63 68 M 61 62 Q 67 64.6 74 61.5" fill="none" stroke="#E8935A" strokeWidth="1" strokeLinecap="round" opacity="0.35" />
      {/* épave : coque + mâts + voiles (silhouette conservée) + braises en rim côté feu */}
      <path d="M 4 82 L 46 82 L 42 90 L 10 90 Z" fill="#0A0E12" />
      <path d="M 17 82 L 17 48 L 19 48 L 19 82 Z M 33 82 L 33 54 L 35 54 L 35 82 Z" fill="#0A0E12" />
      <path d="M 18 50 Q 28 56 18 64 Z M 34 56 Q 43 61 34 68 Z M 18 66 Q 27 71 18 78 Z" fill="#0A0E12" />
      <path d="M 44 82.4 L 42.4 89.2" fill="none" stroke="#E8935A" strokeWidth="1" strokeLinecap="round" opacity="0.7" />
      <path d="M 35 55 L 35 81" fill="none" stroke="#E8935A" strokeWidth="0.8" strokeLinecap="round" opacity="0.45" />
      <path d="M 44 80 Q 50 78 54 80 Q 51 74 46 75 Q 48 70 54 70 Q 46 66 42 72 Q 40 77 44 80 Z" fill={`url(#${uid}-fumee)`} opacity="0.55" />
      {/* flammes à 2 valeurs : base orange + cœur clair */}
      <path d="M 46 84 L 58 81 L 50 87 L 60 88 L 47 89 Z" fill="#E8935A" opacity="0.8" />
      <path d="M 42 76 L 52 73 L 45 78 L 54 80 L 42 80 Z" fill="#E8935A" opacity="0.8" />
      <path d="M 47.5 84.6 L 54 83 L 50 86.4 L 55 87.2 L 48 87.8 Z" fill="#F5CD6B" opacity="0.9" />
      <path d="M 44.5 76.8 L 50 75.2 L 46.5 78 L 51 79.2 L 44.5 79.4 Z" fill="#F5CD6B" opacity="0.85" />
      {/* plan 3 : eau noire + reflets du feu + houle froide + vignette */}
      <path d="M 0 94 Q 20 90 44 93 Q 76 89 120 94 L 120 120 L 0 120 Z" fill="#0D141A" opacity="0.75" />
      <path d="M 20 98 Q 32 95 44 98 M 30 106 Q 40 103 50 106" fill="none" stroke="#E8935A" strokeWidth="1.4" strokeLinecap="round" opacity="0.4" />
      <path d="M 62 96 Q 76 92 90 96 M 96 102 Q 106 99 116 102" fill="none" stroke="#5FBDD0" strokeWidth="1.8" strokeLinecap="round" opacity="0.3" />
      <rect x="0" y="88" width="120" height="32" fill={`url(#${uid}-vign)`} />
    </>
  ),
}
